/**
 * AboutSectionCard
 *
 * One About section — a title above a rounded card whose item strings are
 * rendered as plain text with a thin rule between items (but not after the
 * last). Web port of Flutter's `AboutListItem`
 * (`lib/presentation/screens/about/about_list_item.dart:6-72`).
 *
 * Flutter renders each item as plain `Text(item, style: aboutItemTitle)` with
 * a `separatorColor` rule between items only (`about_list_item.dart:46-63`) —
 * no bullet markers. The retired web About dialog prefixed each item with a
 * `•`; this component drops that to match Flutter's plain-text layout.
 *
 * UI deviates from Flutter only in styling tokens (theme/responsive per
 * `presentation.md`): the between-item rule reuses the shared `Divider`
 * component's `margin` mode — a `theme.separator` rule, the web equivalent of
 * Flutter's thin `separatorColor` line — instead of hand-rolling one.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { AboutSectionEntity } from '@/domain/entities/about/aboutSectionEntity';
import { Divider } from '@/presentation/components/common/Divider';

export interface AboutSectionCardProps {
    section: AboutSectionEntity;
    /** Stable index for the per-section QA id (`about-section-<index>`). */
    index: number;
}

export const AboutSectionCard = ({ section, index }: AboutSectionCardProps) => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();

    return (
        <div
            data-qa-id={`about-section-${index}`}
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Section title above the card (Flutter: padding bottom 8 / left 8,
                normal-weight `aboutSectionTitle`). */}
            <span
                style={{
                    paddingBottom: responsive.spacing.sm,
                    paddingLeft: responsive.spacing.sm,
                    fontSize: responsive.font['2xl'],
                    fontWeight: responsive.fontWeight.regular,
                    color: theme.text.primary,
                }}
            >
                {section.title}
            </span>

            {/* Rounded container holding the plain-text items. */}
            <div
                style={{
                    backgroundColor: theme.surface,
                    borderRadius: responsive.borderRadius.md,
                    padding: responsive.spacing.lg,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {section.items.map((item, itemIndex) => (
                    <div key={item}>
                        <span
                            style={{
                                display: 'block',
                                fontSize: responsive.font.lg,
                                fontWeight: responsive.fontWeight.regular,
                                color: theme.text.secondary,
                            }}
                        >
                            {item}
                        </span>
                        {/* Separator between items only — not after the last
                            (Flutter: `index != items.length - 1`). */}
                        {itemIndex !== section.items.length - 1 && (
                            <Divider margin={responsive.spacing.lg} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
